// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The model, pinned.
 *
 * Plain `.mjs` with a `.d.ts` sibling rather than TypeScript, because
 * `scripts/make-reference.mjs` runs under bare Node with no TypeScript loader and MUST read the
 * same constants the application does. If the reference vector were ever generated against a
 * different revision than the tool loads, the self-check would compare two different models and
 * either pass wrongly or fail wrongly.
 */

/** MobileCLIP-S0's vision tower, converted to ONNX by Xenova. Public and ungated. */
export const MODEL_ID = 'Xenova/mobileclip_s0';

/**
 * Pinned to an immutable commit rather than `main`.
 *
 * A repository owner can replace `main` at any time, and this tool's entire output depends on the
 * weights being the ones its reference vector was computed from.
 */
export const MODEL_REVISION = '757d59c9c6870a76a4b0306f05f5061bca15c39f';

/**
 * fp16, and this is the load-bearing decision in the whole tool.
 *
 * The 11.8 MB `_quantized` (int8) export of this vision tower is NUMERICALLY BROKEN: measured
 * cosine 0.09 against its own fp32 output, and four unrelated photographs come back with pairwise
 * cosines of 0.73-0.88 where fp32 gives 0.08-0.17. Every image looks like every other image. The
 * repository's own `config.json` quietly steers around it by declaring
 * `transformers.js_config.dtype.vision_model = "fp32"`.
 *
 * MobileCLIP-S0 is a FastViT-style convolutional hybrid with squeeze-and-excite blocks, which
 * per-tensor int8 quantisation handles badly; the same int8 recipe is fine on a plain ViT.
 *
 * fp16 is 22,876,479 bytes, measures 0.99999 against fp32, and is half the download. And on the
 * single-threaded WebAssembly tier that a static host forces, quantisation buys no speed at all —
 * measured medians per image at 256x256 were fp32 137 ms, fp16 144 ms, q4 145 ms, q8 171 ms. The
 * smallest file is also the slowest AND the wrong answer.
 */
export const MODEL_DTYPE = 'fp16';

/** Bytes of the file above. Shown to the user BEFORE the download starts, not after. */
export const MODEL_BYTES = 22876479;

/** Embedding width of both towers. */
export const EMBED_DIM = 512;

/** Human-readable, for the About and Privacy modals. */
export const MODEL_LICENSE = 'Apple ML Research MobileCLIP — see the model card';
export const MODEL_URL = 'https://huggingface.co/Xenova/mobileclip_s0';
